<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: "Inventory",
      text: "Inventory",
      search: '',
      data: [],
      pagination: {
        current_page: 1,
        per_page: 20,
        last_page: 1,
        total: 0,
        links: []
      },
      app_currency: '',
      selected_rows: [],

      // Modal state
      showModal: false,
      modalAction: '',
      modalLoading: false,
      form: {
        remark: '',
        document: null,
      },
      documentPreview: null,
    };
  },

  computed: {
    text1() {
      return `Pending Variances (${this.pagination.total})`;
    },
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },
    allSelected() {
      return this.data?.length && this.selected_rows.length === this.data.length;
    },
    anySelected() {
      return this.selected_rows.length > 0;
    },
    modalTitle() {
      return this.modalAction === 'approve' ? 'Approve Variances' : 'Reject Variances';
    },
    modalBtnClass() {
      return this.modalAction === 'approve' ? 'btn-success' : 'btn-danger';
    },
  },

  async mounted() {
    this.pendingVariances();
    this.app_currency = await this.$appCurrency();
  },

  methods: {
    async pendingVariances(page = 1) {
      this.setLoading(true);
      try {
        const params = {
          page,
          po_number: this.search.trim() || ''
        };
        const query_string = new URLSearchParams(params).toString();
        const response = await axios.get(`/inventory/stocks/variances/pending?${query_string}`);

        this.data = response.data.data.data.data;
        this.pagination = {
          current_page: response.data.data.data.current_page,
          per_page: response.data.data.data.per_page,
          last_page: response.data.data.data.last_page,
          total: response.data.data.data.total,
          links: response.data.data.data.links
        };
        this.selected_rows = [];
      } catch (error) {
        this.$swalToast(error.response?.data?.message || 'Error loading variances', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    handleToggleAll(event) {
      if (event.target.checked) {
        this.selected_rows = this.data.map(row => row.id);
      } else {
        this.selected_rows = [];
      }
    },

    approveSelected() {
      if (this.selected_rows.length === 0) {
        this.$swalToast('Select items to approve', 'warning');
        return;
      }
      this.modalAction = 'approve';
      this.form.remark = '';
      this.form.document = null;
      this.documentPreview = null;
      this.showModal = true;
    },

    rejectSelected() {
      if (this.selected_rows.length === 0) {
        this.$swalToast('Select items to reject', 'warning');
        return;
      }
      this.modalAction = 'reject';
      this.form.remark = '';
      this.form.document = null;
      this.documentPreview = null;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.form.remark = '';
      this.form.document = null;
      this.documentPreview = null;
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.form.document = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.documentPreview = {
          url: e.target.result,
          name: file.name,
          isPdf: file.type === 'application/pdf',
        };
      };
      reader.readAsDataURL(file);
    },

    async submitModal() {
  if (!this.form.remark.trim()) {
    this.$swalToast('Remark is required', 'warning');
    return;
  }

  const formData = new FormData();
  this.selected_rows.forEach(id => {
    formData.append('purchase_receipt_item_ids[]', id);
  });
  formData.append('remark', this.form.remark);
  if (this.form.document) {
    formData.append('document', this.form.document);
  }

  this.modalLoading = true;
  try {
    const endpoint = this.modalAction === 'approve'
      ? '/inventory/stocks/variances/approve'
      : '/inventory/stocks/variances/reject';

    await axios.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const msg = this.modalAction === 'approve'
      ? 'Variances approved successfully!'
      : 'Variances rejected!';
    this.$swalToast(msg, 'success');
    this.closeModal();
    this.selected_rows = [];
    this.pendingVariances();
  } catch (error) {
    this.$swalToast(error.response?.data?.message || 'Something went wrong', 'error');
  } finally {
    this.modalLoading = false;
  }
},

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.pendingVariances(page);
    },

    clearFilter() {
      this.search = '';
      this.pendingVariances();
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="me-3" v-if="anySelected">
            <span class="badge bg-info">{{ selected_rows.length }} items selected</span>
            <button class="btn btn-sm btn-success ms-2" @click="approveSelected">
              <i class="ti ti-check"></i> Approve
            </button>
            <button class="btn btn-sm btn-danger ms-1" @click="rejectSelected">
              <i class="ti ti-x"></i> Reject
            </button>
            <button class="btn btn-sm btn-outline-secondary ms-1" @click="selected_rows = []">
              <i class="ti ti-x"></i> Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Card -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h5>Pending Variances List</h5>
          <div class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
            <div class="me-3" v-if="search">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()">
                <i class="ti ti-filter-x"></i>
              </a>
            </div>
            <div class="me-3">
              <input
                type="text"
                class="form-control"
                v-model="search"
                @keyup.enter="pendingVariances"
                placeholder="Search PO Number..."
              />
            </div>
            <div class="me-3">
              <button type="button" class="btn btn-dark" @click="pendingVariances()">
                <i class="ti ti-search"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="card-body card-table-wrapper">
          <div class="custom-datatable-filter table-responsive">
            <table class="card-table" v-if="data.length">
              <thead>
                <tr>
                  <th>
                    <div class="form-check form-checkbox-success mb-2">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="allSelected"
                        @change="handleToggleAll"
                      />
                    </div>
                  </th>
                  <th>#</th>
                  <th>PO Number</th>
                  <th>Item</th>
                  <th>PO Price</th>
                  <th>Received Price</th>
                  <th>Variance %</th>
                  <th>Qty Received</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in data" :key="row.id">
                  <td>
                    <div class="form-check form-checkbox-success mb-2">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :value="row.id"
                        v-model="selected_rows"
                      />
                    </div>
                  </td>
                  <td>{{ serialNo + index + 1 }}.</td>
                  <td>
                    <h6>
                      <a
                        :href="`/inventory/purchase-order/view/${row.purchase_order_item.purchase_order.public_token}`"
                        target="_blank"
                        class="text-primary"
                      >
                        {{ row.purchase_order_item.purchase_order.po_number }}
                      </a>
                    </h6>
                  </td>
                  <td>{{ row.item?.name }}</td>
                  <td>{{ app_currency?.symbol + parseInt(row.po_price) }}</td>
                  <td>{{ app_currency?.symbol + parseInt(row.unit_price) }}</td>
                  <td :class="row.variance_pct > 0 ? 'text-danger' : 'text-success'">
                    {{ row.variance_pct }}%
                  </td>
                  <td>{{ parseFloat(row.quantity_received).toFixed(2) }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="row.status === 'pending' ? 'bg-warning text-dark' : 'bg-success'"
                    >
                      {{ row.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-else class="p-3 text-center text-muted">
              No pending variances found. All stock received within tolerance!
            </div>
          </div>

          <!-- Pagination -->
          <nav v-if="pagination.links.length > 3" class="m-3">
            <ul class="pagination pagination-sm">
              <li
                v-for="(link, index) in pagination.links"
                :key="index"
                :class="['page-item', { active: link.active, disabled: !link.url }]"
              >
                <a
                  class="page-link"
                  href="javascript:void(0)"
                  v-html="link.label"
                  @click="changePage(link.url)"
                ></a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

    </div>
  </div>

  <!-- Approve / Reject Modal -->
  <div v-if="showModal" class="modal fade show d-block" style="background:rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content">

        <div class="modal-header">
          <h5 class="modal-title">{{ modalTitle }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Info -->
          <div class="alert alert-info py-2">
            <i class="ti ti-info-circle me-1"></i>
            {{ selected_rows.length }} item(s) selected
          </div>

          <!-- Remark -->
          <div class="mb-3">
            <label class="form-label">Remark <span class="text-danger">*</span></label>
            <textarea
              class="form-control"
              rows="3"
              v-model="form.remark"
              placeholder="Enter remark..."
            ></textarea>
          </div>

          <!-- Document Upload -->
          <!-- <div class="mb-3">
            <label class="form-label">Document </label>
            <input
              type="file"
              class="form-control"
              accept="image/*,.pdf"
              @change="handleFileChange"
            />
            <small class="text-muted">PDF, JPG, PNG</small>
          </div> -->

          <!-- Preview -->
          <div v-if="documentPreview" class="mt-2">
            <div v-if="documentPreview.isPdf" class="border rounded p-2 text-center">
              <i class="ti ti-file-type-pdf text-danger" style="font-size:32px;"></i>
              <p class="mb-0 mt-1 small">{{ documentPreview.name }}</p>
            </div>
            <div v-else>
              <img :src="documentPreview.url" class="img-fluid rounded border" style="max-height:180px;" />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="closeModal" :disabled="modalLoading">
            Cancel
          </button>
          <button :class="`btn ${modalBtnClass}`" @click="submitModal" :disabled="modalLoading">
            <span v-if="modalLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else :class="`ti ${modalAction === 'approve' ? 'ti-check' : 'ti-x'} me-1`"></i>
            {{ modalTitle }}
          </button>
        </div>

      </div>
    </div>
  </div>

  <app-footer></app-footer>
</template>